import axios, { AxiosProgressEvent, AxiosRequestConfig, Method } from "axios";
import { serverEndPoint, serverEndPointForAuth } from "./EnvironmentVariables";
import Logger from "../../Logger";

// eslint-disable-next-line no-magic-numbers
const ONE_MINUTE_MS = 60 * 1000;
const DEFAULT_TIMEOUT_MS = ONE_MINUTE_MS;
// eslint-disable-next-line no-magic-numbers
const UPLOAD_TIMEOUT_MS = 15 * ONE_MINUTE_MS;

export type FetchRequestType = "withData" | "noData" | "auth";

// Default T is `any` so existing untyped callers keep their loose data access.
// Pass an explicit generic — e.g. FetchData<MyResponse>(...) — to opt into typed responses.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type FetchResult<T = any> =
	| { status: true; data: T }
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	| { status: false; data: { detail: string } | any };

type FetchDataPropType = {
	requestType: FetchRequestType;
	method: Method;
	url: string;
	data?: unknown;
	headers: Record<string, string>;
	token?: string;
	onUploadProgress?: (event: AxiosProgressEvent) => void;
};

const resolveBaseUrl = (requestType: FetchRequestType): string => {
	if (requestType === "auth") return serverEndPointForAuth ?? "";
	return serverEndPoint ?? "";
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FetchData = async <T = any>(
	props: FetchDataPropType
): Promise<FetchResult<T>> => {
	const { requestType, method, url, headers, data, onUploadProgress } = props;

	const isUpload = requestType === "withData" && Boolean(onUploadProgress);
	const config: AxiosRequestConfig = {
		method,
		url: resolveBaseUrl(requestType) + url,
		headers,
		timeout: isUpload ? UPLOAD_TIMEOUT_MS : DEFAULT_TIMEOUT_MS,
		...(requestType !== "noData" ? { data } : {}),
		...(onUploadProgress ? { onUploadProgress } : {}),
	};

	try {
		const res = await axios.request<T>(config);
		return { status: true, data: res.data };
	} catch (err: unknown) {
		Logger("error", "FetchData failed", err);
		const responseData =
			(err as { response?: { data?: unknown } } | undefined)?.response?.data;
		if (responseData !== undefined) {
			return { status: false, data: responseData as T };
		}
		return { status: false, data: { detail: "Unknown error" } };
	}
};

export default FetchData;
